import {Component, ElementRef, Input, inject, OnInit, OnDestroy, ViewChild} from "@angular/core";
import {Router} from "@angular/router";
import {BreadcrumbItem} from "src/app/core/models/BreadcrumbItem";
import {Employee} from "src/app/core/models/Employee";
import {EmployeeService} from "src/app/core/services/services/employee.service";
import {DataService} from "../../../services/services/data.service";
import {EmployeeFullName} from "../../../models/EmployeeFullName";
import {UntypedFormControl, ReactiveFormsModule} from "@angular/forms";
import {filter, tap} from "rxjs/operators";
import {Observable} from "rxjs";
// eslint-disable-next-line @typescript-eslint/naming-convention
import Config from "../../../config/Config";
import {CustomizingService} from "../../../services/services/customizing.service";
import {MatFormField, MatSuffix} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatAutocompleteTrigger} from "@angular/material/autocomplete";
import {SanitizeInputDirective} from "../../../../shared/inputdirectives/sanitize-input.directive";
import {MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {EmployeeAutocompleteComponent} from "../../../../shared/autocomplete/components/employee-autocomplete.component";
import {AsyncPipe} from "@angular/common";

@Component({
	selector: "app-breadcrumb-emp-search",
	templateUrl: "./breadcrumb-emp-search.component.html",
	styleUrls: ["./breadcrumb-emp-search.component.scss"],
	imports: [
		MatFormField,
		MatInput,
		MatAutocompleteTrigger,
		ReactiveFormsModule,
		SanitizeInputDirective,
		MatIconButton,
		MatSuffix,
		MatIcon,
		EmployeeAutocompleteComponent,
		AsyncPipe,
	],
})
export class BreadcrumbEmpSearchComponent implements OnInit, OnDestroy {
	@Input() breadcrumb: BreadcrumbItem;
	currentEmployeeObservable: Observable<Employee>;
	lastEmployee: Employee;
	employeeForm: UntypedFormControl = new UntypedFormControl();
	readonly detailPath: string = "/employee-edit/";

	@ViewChild("employeeInput") employeeInput: ElementRef;
	@ViewChild(EmployeeAutocompleteComponent) employeeAutocomplete: EmployeeAutocompleteComponent;

	private readonly _employeeService = inject(EmployeeService);
	private readonly _router = inject(Router);
	private readonly _dataService = inject(DataService);
	private readonly _customizingService = inject(CustomizingService);

	ngOnInit() {
		this.fetchCurrentEmployeeFromUrlParam();

		this.currentEmployeeObservable = this._dataService.currentEmployee.pipe(
			filter((e) => e !== undefined),
			tap((currentEmployee) => {
				this.updateCurrentEmployee(currentEmployee);
			}),
		);
	}

	private updateCurrentEmployee(currentEmployee: Employee) {
		this.lastEmployee = currentEmployee;
		this.updateCurrentEmployeeForm(currentEmployee);
		this.employeeAutocomplete?.clearOptions();
	}

	private fetchCurrentEmployeeFromUrlParam() {
		const subPath = this.breadcrumb.path.substring(
			this.breadcrumb.path.lastIndexOf(this.detailPath) + this.detailPath.length,
		);
		const id =
			subPath.lastIndexOf("/") > 0
				? Number.parseInt(subPath.substring(0, subPath.lastIndexOf("/")), 10)
				: Number.parseInt(subPath, 10);
		if (!Number.isNaN(id)) {
			this._customizingService.getCurrentClient().then((client) => {
				this._employeeService
					.getEmployee(Config.services.employees.baseUrl + id, client, null, false)
					.then((employee) => this._dataService.changeEmployee(employee));
			});
		}
	}

	updateCurrentEmployeeForm(employee: Employee): void {
		this.employeeForm.setValue(this.getEmployeeDisplayValue(employee), {emitEvent: false});
	}

	onItemChange(employee: EmployeeFullName) {
		this._employeeService.getEmployeeById(employee.id).subscribe((selectedEmployee) => {
			this._dataService.changeEmployee(selectedEmployee);
		});

		this._router.navigate([this.detailPath + employee.id]);
	}

	onClear() {
		this.employeeForm.reset();
		this.employeeAutocomplete?.clearOptions();
	}

	onLeaveText() {
		// deferred so a click on an autocomplete option (which blurs the input first) is processed before we reset the value
		setTimeout(() => {
			if (this.lastEmployee) {
				this.updateCurrentEmployeeForm(this.lastEmployee);
			}
		});
	}

	private getEmployeeDisplayValue(employee: Employee): string {
		if (!employee) {
			return "";
		}

		return employee.personnelNumber
			? `${employee.firstName} ${employee.lastName}, ${employee.personnelNumber}`
			: `${employee.firstName} ${employee.lastName}`;
	}

	ngOnDestroy() {
		this.lastEmployee = null;
		this.employeeForm.reset();
		this._dataService.clearEmployeeCache();
	}
}
